import { useEffect, useRef } from 'react'
import type { VocabularyEntry, VocabularyCategory } from '@/db/Types'
import { VOCABULARY_CATEGORY_LABELS } from '@/constants/VocabularyLabels'
import '../ui/shared-utilities.css'
import './search-dropdown.css'

interface PlayMatch {
	id: string
	name: string
	playbookName?: string
}

interface SearchDropdownProps {
	suggestions: VocabularyEntry[]
	playResults: PlayMatch[]
	isSearching: boolean
	query: string
	highlightedIndex: number
	onSuggestionSelect: (entry: VocabularyEntry) => void
	onPlaySelect: (playId: string) => void
	onClose: () => void
}


/** Maps vocabulary category to the CSS badge modifier class. */
function badgeClass(category: VocabularyCategory): string {
	if (category === 'formation') return 'search-dropdown__badge--formation'
	if (category === 'modifier') return 'search-dropdown__badge--modifier'
	// route, blocking, motion, protection are all concept categories
	return 'search-dropdown__badge--concept'
}

export function SearchDropdown({
	suggestions,
	playResults,
	isSearching,
	query,
	highlightedIndex,
	onSuggestionSelect,
	onPlaySelect,
	onClose,
}: SearchDropdownProps) {
	const containerRef = useRef<HTMLDivElement>(null)

	// Scroll highlighted item into view when highlightedIndex changes
	useEffect(() => {
		if (highlightedIndex < 0 || !containerRef.current) return
		const highlighted = containerRef.current.querySelector<HTMLElement>(
			'.search-dropdown__item--highlighted',
		)
		highlighted?.scrollIntoView({ block: 'nearest' })
	}, [highlightedIndex])

	if (!query.trim()) return null

	const hasSuggestions = suggestions.length > 0
	const hasPlays = playResults.length > 0
	const hasResults = hasSuggestions || hasPlays

	if (!hasResults && isSearching) {
		return (
			<div className="search-dropdown search-dropdown--loading dropdown-positioned">
				<span className="search-dropdown__empty-text">Searching...</span>
			</div>
		)
	}

	if (!hasResults) {
		return (
			<div className="search-dropdown search-dropdown--empty dropdown-positioned">
				<span className="search-dropdown__empty-text">
					No results for &ldquo;{query}&rdquo;
				</span>
			</div>
		)
	}

	return (
		<div
			ref={containerRef}
			className="search-dropdown dropdown-positioned"
			role="listbox"
		>
			{hasSuggestions && (
				<div className="search-dropdown__section">
					<div className="search-dropdown__section-title">
						Vocabulary Matches
					</div>
					{suggestions.map((entry, index) => {
						const isHighlighted = index === highlightedIndex
						return (
							<button
								key={entry.id}
								role="option"
								aria-selected={isHighlighted}
								onClick={() => {
									onSuggestionSelect(entry)
									onClose()
								}}
								className={`search-dropdown__item${isHighlighted ? ' search-dropdown__item--highlighted' : ''}`}
							>
								<div className="search-dropdown__item-content">
									<span className="search-dropdown__item-code">
										{entry.code}
									</span>
									<span className="search-dropdown__item-name">
										{entry.display_name ?? ''}
									</span>
									<span
										className={`search-dropdown__item-badge ${badgeClass(entry.category)}`}
									>
										{VOCABULARY_CATEGORY_LABELS[entry.category]}
									</span>
								</div>
							</button>
						)
					})}
				</div>
			)}

			{hasPlays && (
				<div className="search-dropdown__section">
					<div className="search-dropdown__section-title">
						Existing Plays
					</div>
					{playResults.map((play, index) => {
						const flatIndex = suggestions.length + index
						const isHighlighted = flatIndex === highlightedIndex
						return (
							<button
								key={play.id}
								role="option"
								aria-selected={isHighlighted}
								onClick={() => {
									onPlaySelect(play.id)
									onClose()
								}}
								className={`search-dropdown__item${isHighlighted ? ' search-dropdown__item--highlighted' : ''}`}
							>
								<div className="search-dropdown__item-content">
									<span className="search-dropdown__item-name">
										&ldquo;{play.name}&rdquo;
									</span>
									{play.playbookName && (
										<span className="search-dropdown__item-playbook">
											&mdash; {play.playbookName}
										</span>
									)}
								</div>
							</button>
						)
					})}
				</div>
			)}
		</div>
	)
}

export type { PlayMatch, SearchDropdownProps }
